import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../bom/ui/Toast';
import { sbomApi } from './sbomApi';

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Unknown'];
const VERDICTS = [['unassessed', 'Needs assessment'], ['investigating', 'Under investigation'],
  ['affected', 'Affected'], ['not_affected', 'Not affected']];
const KEV = [['any', 'Any'], ['only', 'Only those on the KEV list'], ['also', 'Also every one on the KEV list']];

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

/**
 * Write to Orcanos — choose WHICH findings become Orcanos Vulnerability items (severity, KEV,
 * verdict, release), see what that means, then write them in one batch (background, one at a time).
 * Items already in Orcanos whose content changed are refreshed too. An admin can save the conditions
 * as the rule: the batch's default, and what the Assess drawer's "Save to Orcanos" tick starts as.
 */
export default function SbomVulnOrcanos({ releases, onClose, onDone, onAuth }) {
  const { showToast } = useToast();
  const [base, setBase] = useState(null);       // GET /vulns/orcanos
  const [rule, setRule] = useState(null);
  const [update, setUpdate] = useState(true);
  const [plan, setPlan] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    sbomApi.orcanos()
      .then((d) => { setBase(d); setRule(d.rule); setPlan(d.plan); if (d.job?.running) setJob(d.job); })
      .catch((e) => { if (!onAuth(e)) setBase({ error: e.message }); });
  }, [onAuth]);

  // The preview follows the conditions (debounced).
  useEffect(() => {
    if (!rule || job?.running) return undefined;
    const t = setTimeout(() => {
      sbomApi.orcanosPreview(rule, update).then(setPlan).catch((e) => { if (!onAuth(e)) setPlan({ error: e.message }); });
    }, 250);
    return () => clearTimeout(t);
  }, [rule, update, job?.running, onAuth]);

  const running = !!job?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      sbomApi.orcanos().then((d) => {
        setJob(d.job);
        if (!d.job?.running) { setPlan(d.plan); doneRef.current?.(); }
      }).catch((e) => onAuth(e));
    }, 1000);
    return () => clearInterval(t);
  }, [running, onAuth]);

  const set = (patch) => setRule((r) => ({ ...r, ...patch }));
  const write = () => {
    setBusy(true);
    sbomApi.orcanosWrite(rule, update).then((d) => setJob(d.job))
      .catch((e) => { if (!onAuth(e)) showToast(e.message, 'error'); })
      .finally(() => setBusy(false));
  };
  const saveRule = () => sbomApi.orcanosRule(rule)
    .then((d) => { setBase((b) => ({ ...b, rule: d.rule })); showToast('Saved as the rule.', 'success'); })
    .catch((e) => { if (!onAuth(e)) showToast(e.message, 'error'); });
  const stop = () => sbomApi.orcanosStop().then((d) => setJob(d.job)).catch((e) => showToast(e.message, 'error'));

  const c = plan?.counts;
  const n = plan?.total || 0;
  const pct = job?.total ? Math.max(3, Math.round(((job.done || 0) / job.total) * 100)) : 3;
  const ruleChanged = base?.rule && rule && JSON.stringify(base.rule) !== JSON.stringify(rule);

  return (
    <div className="sbom-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !running) onClose(); }}>
      <section className="sbom-modal" style={{ width: 'min(860px, 100%)' }} role="dialog" aria-modal="true" aria-labelledby="sbom-vo-title">
        <div className="sbom-modal-head">
          <div>
            <h3 id="sbom-vo-title">Write vulnerabilities to Orcanos</h3>
            <div className="sbom-tiny sbom-muted">Choose which findings become {base?.label || 'Vulnerability'} items, traced to their risk and
              the affected components. The Software BOM keeps them up to date; edits made in Orcanos are overwritten.</div>
          </div>
          <button type="button" className="sbom-modal-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="sbom-modal-body">
          {base?.error && <div className="sbom-error" role="alert">{base.error}</div>}
          {base && !base.error && !base.enabled && (
            <div className="sbom-warn">Choose the <b>Vulnerability work item type</b> in Settings → Software BOM first.</div>
          )}
          {base?.enabled && rule && !job && (
            <>
              <div className="sbom-vo-grid">
                <fieldset className="sbom-vo-set">
                  <legend>Severity <span className="sbom-muted">(none ticked = any)</span></legend>
                  {SEVERITIES.map((s) => (
                    <label key={s} className="sbom-check"><input type="checkbox" checked={rule.severities.includes(s)}
                      onChange={() => set({ severities: toggle(rule.severities, s) })} />
                      <span className={`sbom-chip sbom-chip--${s}`}>{s}</span></label>
                  ))}
                </fieldset>
                <fieldset className="sbom-vo-set">
                  <legend>Assessment <span className="sbom-muted">(none ticked = any)</span></legend>
                  {VERDICTS.map(([k, l]) => (
                    <label key={k} className="sbom-check"><input type="checkbox" checked={rule.verdicts.includes(k)}
                      onChange={() => set({ verdicts: toggle(rule.verdicts, k) })} />{l}</label>
                  ))}
                </fieldset>
                <fieldset className="sbom-vo-set">
                  <legend>Actively exploited (KEV)</legend>
                  <select className="sbom-select" value={rule.kev} aria-label="KEV" onChange={(e) => set({ kev: e.target.value })}>
                    {KEV.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <legend style={{ marginTop: 10 }}>Release</legend>
                  <select className="sbom-select" value={rule.release} aria-label="Release" onChange={(e) => set({ release: e.target.value })}>
                    <option value="">All releases</option>
                    {releases.map((r) => <option key={r.item_id} value={r.item_id}>{r.name}</option>)}
                  </select>
                </fieldset>
              </div>
              <label className="sbom-check" style={{ margin: '10px 0' }}>
                <input type="checkbox" checked={update} onChange={(e) => setUpdate(e.target.checked)} />
                Also update items already in Orcanos whose vulnerability or assessment changed
              </label>
              {plan?.error && <div className="sbom-error" role="alert">{plan.error}</div>}
              {c && (
                <div className="sbom-vo-sum" role="status">
                  <b>{c.match}</b> finding{c.match === 1 ? '' : 's'} match · <b>{c.new}</b> new to write ·{' '}
                  <b>{c.changed}</b> in Orcanos and changed · <b>{c.current}</b> in Orcanos and up to date
                </div>
              )}
              {plan?.items?.length > 0 && (
                <div className="sbom-tw" style={{ maxHeight: 260 }}>
                  <table className="sbom-table">
                    <thead><tr><th /><th>Vulnerability</th><th>Package</th><th>Severity</th><th>Assessment</th></tr></thead>
                    <tbody>
                      {plan.items.map((x) => (
                        <tr key={`${x.vuln_id}|${x.package_key}`}>
                          <td>{x.action === 'new' ? <span className="sbom-pill sbom-pill--info">New</span>
                            : <span className="sbom-pill sbom-pill--warn" title={`Update ${x.key}`}>Update {x.key}</span>}</td>
                          <td className="sbom-mono">{x.vuln_id}</td>
                          <td>{x.package}</td>
                          <td><span className={`sbom-chip sbom-chip--${x.severity}`}>{x.severity}</span>{x.kev && <span className="sbom-kev">KEV</span>}</td>
                          <td><span className={`sbom-pill sbom-pill--${x.verdict.tone}`}>{x.verdict.label}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {n > plan.items.length && <div className="sbom-tiny sbom-muted">…and {n - plan.items.length} more.</div>}
                </div>
              )}
            </>
          )}
          {job && (
            <div role="status">
              <div className="sbom-scanprog">
                <span className="sbom-prog" title={`${pct}%`}><i style={{ width: `${running ? pct : 100}%` }} /></span>
                <span className="sbom-tiny">{job.stage} · {job.done || 0} of {job.total}</span>
              </div>
              <p className="sbom-tiny">{job.created || 0} created · {job.updated || 0} updated · {job.current || 0} already up to date
                {job.failed ? ` · ${job.failed} failed` : ''}</p>
              {job.errors?.length > 0 && (
                <ul className="sbom-tiny sbom-badtext">{job.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
            </div>
          )}
        </div>
        <div className="sbom-modal-foot">
          {base?.enabled && !job && base.can_save_rule && (
            <button type="button" className="sbom-btn sbom-btn--ghost" style={{ marginRight: 'auto' }} onClick={saveRule}
              disabled={!ruleChanged} title="Use these conditions by default here and for the Save to Orcanos tick in the Assess drawer">
              {ruleChanged ? 'Save as the rule' : 'Saved as the rule'}</button>
          )}
          {running ? <button type="button" className="sbom-btn" onClick={stop} disabled={job.stage?.startsWith("Stopping")}>Stop</button>
            : <button type="button" className="sbom-btn" onClick={onClose}>{job ? 'Close' : 'Cancel'}</button>}
          {base?.enabled && !job && (
            <button type="button" className="sbom-btn sbom-btn--pri" onClick={write} disabled={busy || !n}>
              {busy ? 'Starting…' : `Write ${n} to Orcanos`}</button>
          )}
        </div>
      </section>
    </div>
  );
}
